/**
 * Wire types for the fleet console.
 *
 * Two naming conventions meet here, deliberately:
 *
 *  - `TaskSpec`, `Schedule`, `RunRecord`, `StepRecord`, `ExtractionRecord`,
 *    `RunStats`, `UiElement` come straight off the Android model
 *    (`core/model/{Task,Run,Ui}.kt`) serialised by kotlinx.serialization with no
 *    naming strategy, so their keys are **camelCase** and the server passes them
 *    through verbatim ("byte-identical to the Android model", rest-api.md §Tasks).
 *
 *  - Everything the *server* owns — `DeviceSummary`, `DeviceDetail`, dispatch,
 *    overview, pagination envelopes — is **snake_case**, as written in
 *    `protocol/rest-api.md`.
 *
 * Do not "tidy" one into the other: the mismatch is the contract.
 */

/* ------------------------------------------------------------------ *
 * Enums (Task.kt / Run.kt)
 * ------------------------------------------------------------------ */

export type DeviceMode = "DEDICATED" | "PERSONAL";
export type VisionMode = "OFF" | "AUTO" | "ALWAYS";
export type ThinkingPolicy = "ADAPTIVE" | "ALWAYS_ON" | "ALWAYS_OFF";
export type ScheduleType = "NONE" | "INTERVAL" | "DAILY" | "WEEKLY";
// Mirrors `RunTrigger` in core/model/Run.kt. REMOTE — dispatched from this console — was
// missing, so every dispatched run was labelled 排程 and an operator would go looking for a
// schedule that does not exist.
export type RunTrigger = "MANUAL" | "SCHEDULE" | "REMOTE";

export type RunStatus =
  | "QUEUED"
  | "WAITING_UNLOCK"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "SKIPPED"
  | "CANCELLED";

export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = [
  "SUCCESS",
  "FAILED",
  "SKIPPED",
  "CANCELLED",
];

/** Mirrors `RunStatus.isTerminal`. */
export function isTerminal(s: RunStatus): boolean {
  return TERMINAL_RUN_STATUSES.includes(s);
}

/* ------------------------------------------------------------------ *
 * Task.kt
 * ------------------------------------------------------------------ */

export interface Schedule {
  type: ScheduleType;
  /** "HH:mm" local times, used by DAILY and WEEKLY. */
  timesOfDay: string[];
  /** ISO day numbers 1=Mon .. 7=Sun, used by WEEKLY. */
  daysOfWeek: number[];
  /** Used by INTERVAL. */
  intervalMinutes: number | null;
}

export interface TaskSpec {
  id: string;
  name: string;
  goal: string;
  /** Packages the agent may drive. Empty = any package (global denylist still applies). */
  allowedPackages: string[];
  /** Overrides the default purchase/transfer/delete label block regex. Null = default. */
  blockedLabelsRegex: string | null;
  /** Allow tapping Send/Post/Tweet-style controls in messaging/social apps. */
  allowSend: boolean;
  /** JSON Schema (as a string) for the `extract` tool's `data` argument. Null = free-form object. */
  extractionSchema: string | null;
  schedule: Schedule;
  visionMode: VisionMode;
  thinkingPolicy: ThinkingPolicy;
  maxSteps: number;
  runTimeoutMinutes: number;
  /** Per-task webhook override; null = global setting. */
  webhookUrl: string | null;
  /** PERSONAL-mode devices: wait for unlock instead of skipping. */
  requireUnlock: boolean;
  enabled: boolean;
  createdAt: number;
  updatedAt: number;
}

export const ADHOC_TASK_ID = "adhoc";

export function emptySchedule(): Schedule {
  return { type: "NONE", timesOfDay: [], daysOfWeek: [], intervalMinutes: null };
}

export function emptyTaskSpec(): TaskSpec {
  return {
    id: "",
    name: "",
    goal: "",
    allowedPackages: [],
    blockedLabelsRegex: null,
    allowSend: false,
    extractionSchema: null,
    schedule: emptySchedule(),
    visionMode: "AUTO",
    thinkingPolicy: "ADAPTIVE",
    maxSteps: 40,
    runTimeoutMinutes: 15,
    webhookUrl: null,
    requireUnlock: true,
    enabled: true,
    createdAt: 0,
    updatedAt: 0,
  };
}

/** What `GET /api/v1/tasks` returns: the spec plus the server's own bookkeeping. */
export interface TaskRow extends TaskSpec {
  version: number;
  created_at: string;
  updated_at: string;
  assignment_count: number;
  /** `{{PLACEHOLDER}}` names the spec actually uses, derived server-side on every read. */
  variables: string[];
  /** Stored default value per name; a dispatch may override any of them. */
  variable_defaults: Record<string, string>;
}

/** `POST /api/v1/tasks/draft` — a proposed spec. Nothing is saved until the operator saves it. */
export interface TaskDraft {
  spec: TaskSpec;
  notes: string;
  variables: string[];
  declared_variables: string[];
  errors: Array<{ path: string; message: string }>;
  /** Packages the draft names that no phone we could ask has installed. */
  unknown_packages: string[];
}

export interface TaskAssignment {
  id: string;
  task_id: string;
  group_id: string | null;
  device_ids: string[] | null;
  enabled: boolean;
  next_fire_at: string | null;
}

export interface SchemaValidation {
  ok: boolean;
  errors: Array<{ path: string; message: string }>;
  columns: Array<{ name: string; type: string; required: boolean }>;
}

/* ------------------------------------------------------------------ *
 * Run.kt
 * ------------------------------------------------------------------ */

export interface RunRecord {
  id: string;
  taskId: string;
  taskName: string;
  goal: string;
  trigger: RunTrigger;
  status: RunStatus;
  createdAt: number;
  startedAt: number | null;
  finishedAt: number | null;
  steps: number;
  promptTokens: number;
  completionTokens: number;
  reasoningTokens: number;
  extractions: number;
  error: string | null;
  summary: string | null;
}

/** `GET /api/v1/runs` rows and `GET /api/v1/runs/{id}` both add the device. */
export interface RunRow extends RunRecord {
  device: { id: string; label: string };
  dispatch_id?: string | null;
}

export interface RunDetailResponse extends RunRow {
  extraction_count: number;
}

export interface StepRecord {
  runId: string;
  n: number;
  observationHash: string;
  observationText: string;
  screenshotPath: string | null;
  thinking: boolean;
  reasoningText: string | null;
  /** JSON array of tool calls as returned by the model. */
  toolCallsJson: string;
  /** JSON array of {tool, ok, message, errorCode} in execution order. */
  resultsJson: string;
  latencyMs: number;
  ttftMs: number;
  promptTokens: number;
  completionTokens: number;
  reasoningTokens: number;
  createdAt: number;
}

/**
 * One extracted record as `GET /api/v1/extractions` returns it.
 *
 * Unlike a run or a step this is not the device's shape: `data` is already parsed, and
 * `device_id`, `task_name`, `received_at` and `forwarded_at` are things only the server knows.
 * `data` is whatever the task's extraction schema says — free-form when it has none — so the
 * table generates its columns from the keys actually present rather than from a fixed list.
 */
export interface ExtractionRow {
  id: string;
  device_id: string;
  run_id: string;
  task_id: string | null;
  task_name: string | null;
  schema_version: number;
  data: Record<string, unknown>;
  created_at: string;
  received_at: string;
  forwarded_at: string | null;
}

/** `POST /api/v1/extractions/export` then poll `GET /api/v1/exports/{id}`. */
export interface ExportJobStatus {
  job_id: string;
  status: "PENDING" | "RUNNING" | "DONE" | "FAILED";
  rows: number | null;
  error: string | null;
}

export interface RunStats {
  sinceMillis: number;
  runs: number;
  success: number;
  failed: number;
  cancelled: number;
  skipped: number;
  extractions: number;
  avgSteps: number;
  avgDurationMs: number;
  promptTokens: number;
  completionTokens: number;
  reasoningTokens: number;
  stepsThinkingOn: number;
  stepsThinkingOff: number;
  avgLatencyThinkingOnMs: number;
  avgLatencyThinkingOffMs: number;
  avgPromptTokensPerStep: number;
  avgLatencyFirstStepMs: number;
  avgLatencyLaterStepsMs: number;
}

/* ------------------------------------------------------------------ *
 * Ui.kt
 * ------------------------------------------------------------------ */

export interface UiElement {
  index: number;
  role: string;
  label: string;
  /** Centre in screen pixels. */
  x: number;
  y: number;
  w: number;
  h: number;
  pkg: string;
  windowId: number;
  /** Content hash used to refuse stale indices when the screen moved. */
  token: string;
  clickable: boolean;
  longClickable: boolean;
  editable: boolean;
  scrollable: boolean;
  checkable: boolean;
  checked: boolean;
  selected: boolean;
  focused: boolean;
  password: boolean;
  enabled: boolean;
  viewId: string | null;
}

/**
 * Compact element as it rides a binary screen frame header (fleet-v1.md §7).
 * Phase 2 decodes these; phase 1 only needs the shape to exist.
 */
export interface WireElement {
  i: number;
  r: string;
  l: string;
  x: number;
  y: number;
  w: number;
  h: number;
  t: string;
  /** any of c L e s k K S F p d */
  f: string;
}

export interface ScreenFrameHeader {
  stream_id: string;
  session_id: string;
  frame_no: number;
  at: number;
  format: "jpeg";
  w: number;
  h: number;
  screen_w: number;
  screen_h: number;
  snapshot_id: string;
  pkg: string;
  window_title: string;
  elements: WireElement[];
  notes: string[];
  blocked: string | null;
}

/* ------------------------------------------------------------------ *
 * Actions.kt — the tool calls a step can contain
 * ------------------------------------------------------------------ */

export type ActionDirection = "UP" | "DOWN" | "LEFT" | "RIGHT";
export type ActionKey = "BACK" | "HOME" | "RECENTS" | "NOTIFICATIONS" | "ENTER";

export type AgentActionName =
  | "tap"
  | "tap_at"
  | "type"
  | "scroll"
  | "swipe"
  | "press"
  | "open_app"
  | "wait"
  | "look"
  | "extract"
  | "remember"
  | "done"
  | "fail"
  | "ask_user";

/**
 * `AgentAction` is a Kotlin sealed interface; on the wire a tool call is the raw
 * model function-call JSON — a name plus the arguments of that variant. The union
 * below keeps the argument shapes exact while staying parseable from
 * `StepRecord.toolCallsJson`.
 */
export type AgentAction =
  | { tool: "tap"; index: number; long?: boolean }
  | { tool: "tap_at"; x: number; y: number; long?: boolean }
  | { tool: "type"; index: number | null; text: string; submit?: boolean }
  | { tool: "scroll"; direction: ActionDirection; index?: number | null; amount?: number }
  | { tool: "swipe"; x1: number; y1: number; x2: number; y2: number; durationMs?: number }
  | { tool: "press"; key: ActionKey }
  | { tool: "open_app"; query: string }
  | { tool: "wait"; seconds: number }
  | { tool: "look"; reason: string }
  | { tool: "extract"; data: unknown }
  | { tool: "remember"; note: string }
  | { tool: "done"; summary: string }
  | { tool: "fail"; reason: string }
  | { tool: "ask_user"; question: string };

/** Anything the parser could not place still renders, as a name plus raw args. */
export type ParsedToolCall = { tool: string; args: Record<string, unknown> };

export interface ActionResult {
  tool: string;
  ok: boolean;
  message: string;
  errorCode: string | null;
}

export const TERMINAL_TOOLS: readonly string[] = ["done", "fail", "ask_user"];

/* ------------------------------------------------------------------ *
 * Devices (server-owned, snake_case)
 * ------------------------------------------------------------------ */

export type DeviceHealth = "online" | "stale" | "offline" | "unseen";

export interface ActiveRunRef {
  id: string;
  task_name: string;
  n: number;
  max_steps: number;
}

export interface ControlState {
  enabled: boolean;
  session_id: string | null;
  expires_at: string | null;
}

export interface DeviceSummary {
  id: string;
  label: string;
  model: string;
  android_sdk: number;
  app_version_name: string;
  app_version_code: number;
  group_id: string | null;
  tags: string[];
  online: boolean;
  last_seen_at: string | null;
  health: DeviceHealth;
  battery_pct: number | null;
  charging: boolean;
  screen_on: boolean;
  keyguard_locked: boolean;
  a11y_connected: boolean;
  disarmed: boolean;
  revoked: boolean;
  active_run: ActiveRunRef | null;
  today: { success: number; failed: number };
  control: ControlState;
  config_version: number;
  task_set_version: number;
  pending_commands: number;
}

/**
 * `DeviceDetail` is `DeviceSummary` plus the identity/health/settings blocks the
 * detail page needs. rest-api.md names the type but does not spell out its body;
 * the fields below are taken from the `health` and `config` envelopes in
 * fleet-v1.md §6, which is what the server has to hand. ASSUMPTION — flagged in
 * the handover report.
 */
export interface DeviceDetail extends DeviceSummary {
  manufacturer: string;
  notes: string | null;
  enrolled_at: string | null;
  battery_temp_c: number | null;
  power_source: string | null;
  ime_enabled: boolean;
  shizuku: string;
  free_storage_bytes: number | null;
  network_type: string | null;
  uptime_ms: number | null;
  pending_outbox: number;
  last_run: { id: string; status: RunStatus; finished_at: number | null } | null;
  /** Server-owned config, pushed to the device (fleet-v1.md §6 `config.settings`). */
  settings: Record<string, string | number | boolean | null>;
  /** Keys inside `settings` the fleet owns; everything else is device-local. */
  managed: string[];
  /** Device-local settings the fleet reports but never overwrites. */
  device_local: Record<string, string | number | boolean | null>;
  assigned_tasks: Array<{
    task_id: string;
    task_name: string;
    enabled: boolean;
    next_fire_at: string | null;
  }>;
}

export interface DeviceGroup {
  id: string;
  name: string;
  description: string | null;
  device_count: number;
  default_config: Record<string, unknown> | null;
}

export interface InstalledApp {
  package: string;
  label: string;
}

/* ------------------------------------------------------------------ *
 * Pagination, filters, selection
 * ------------------------------------------------------------------ */

/** Keyset page. There is deliberately no total count anywhere. */
export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

/** The query shape `GET /api/v1/devices` accepts, and what "select all" stores. */
export interface DeviceFilter {
  q?: string;
  health?: DeviceHealth;
  group_id?: string;
  tag?: string;
  has_run?: boolean;
  /** Not in rest-api.md's documented query list; see report. */
  app_version_name?: string;
  sort?: string;
}

/**
 * A bulk selection is either an explicit id list or a **filter descriptor**.
 * Selecting "all 87 matching" must never materialise 87 ids in the client: the
 * set can change between the click and the confirm, and the server is the only
 * thing that can resolve it consistently.
 */
export type Selection =
  | { kind: "ids"; ids: string[] }
  | { kind: "filter"; filter: DeviceFilter; estimated: number };

export interface RunFilter {
  device_id?: string;
  task_id?: string;
  status?: RunStatus;
  dispatch_id?: string;
  since?: string;
  until?: string;
  has_error?: boolean;
}

/* ------------------------------------------------------------------ *
 * Dispatch
 * ------------------------------------------------------------------ */

export type DispatchTarget =
  | { device_ids: string[] }
  | { group_id: string }
  | { filter: DeviceFilter };

export interface DispatchRequest {
  task_id?: string;
  inline_spec?: TaskSpec;
  /** Values for the task's `{{PLACEHOLDERS}}`; overrides its stored defaults for this run. */
  variables?: Record<string, string>;
  target: DispatchTarget;
  stagger_ms: number;
  max_in_flight: number;
  not_before_ms: number | null;
  max_retries: number;
}

export interface DispatchSkip {
  device_id: string;
  reason: string;
}

export interface DispatchPreview {
  affected: number;
  breakdown: { online: number; stale: number; offline: number };
  skipped: DispatchSkip[];
  estimated_seconds: number;
}

export interface DispatchResponse extends DispatchPreview {
  dispatch_id: string;
}

export type DispatchDeviceState =
  | "queued"
  | "sent"
  | "running"
  | "success"
  | "failed"
  | "expired"
  | "skipped";

export interface DispatchStatus {
  id: string;
  task_name: string;
  counts: Record<DispatchDeviceState, number>;
  devices: Array<{
    device_id: string;
    command_id: string | null;
    state: DispatchDeviceState;
    run_id: string | null;
  }>;
}

/* ------------------------------------------------------------------ *
 * Auth
 * ------------------------------------------------------------------ */

export type Role = "viewer" | "operator" | "admin";

export type Permission =
  | "read"
  | "dispatch_run"
  | "stop_run"
  | "remote_control"
  | "edit_task"
  | "manage_device"
  | "enroll_device"
  | "export_data"
  | "danger_zone";

export const ALL_PERMISSIONS: readonly Permission[] = [
  "read",
  "dispatch_run",
  "stop_run",
  "remote_control",
  "edit_task",
  "manage_device",
  "enroll_device",
  "export_data",
  "danger_zone",
];

export interface Operator {
  id: string;
  username: string;
  role: Role;
  permissions: Permission[];
}

/* ------------------------------------------------------------------ *
 * Overview
 * ------------------------------------------------------------------ */

export interface FailureGroup {
  /** The `error` prefix runs were bucketed by, e.g. "E_TIMEOUT". */
  error: string;
  /** Failed runs in the bucket. */
  count: number;
  /** Distinct devices affected — the number that decides whether this is one sick phone or a fleet-wide fault. */
  devices: number;
  task_id?: string | null;
  task_name?: string | null;
  last_at?: string | null;
}

export interface OverviewPayload {
  generated_at: string;
  /** The rollup behind `today` refreshes every 5 minutes; this is how stale it may be. */
  stats_as_of: string;
  devices: {
    total: number;
    online: number;
    stale: number;
    offline: number;
    unseen: number;
    /** Sockets actually open right now, as opposed to "seen recently". */
    connected: number;
  };
  runs_in_flight: {
    running: number;
    waiting_unlock: number;
    queued: number;
    total: number;
  };
  today: {
    runs: number;
    success: number;
    failed: number;
    cancelled: number;
    skipped: number;
    stale: number;
    steps: number;
    extractions: number;
    tokens: { prompt: number; completion: number; reasoning: number; total: number };
    /** Already computed server-side over decided runs only, so it never divides by a pending run. */
    success_rate: number;
    decided: number;
  };
  failures_24h: FailureGroup[];
  /** Devices that have gone quiet, oldest first. */
  quiet_devices: Array<{
    id: string;
    label: string | null;
    model: string | null;
    health: DeviceHealth;
    last_seen_at: string | null;
    group_id: string | null;
  }>;
  schedule_next_hours: Array<{ at: string; runs: number }>;
}

/* ------------------------------------------------------------------ *
 * Fleet settings
 * ------------------------------------------------------------------ */

export interface FleetSettings {
  settings: Record<string, string | number | boolean | null>;
  managed: string[];
  max_concurrent_runs: number;
  default_stagger_ms: number;
  keep_screenshots_days: number;
  retention_days: number;
  disk_usage_bytes: number;
  disk_total_bytes: number;
  agens_key_fingerprint: string | null;
}

/* ------------------------------------------------------------------ *
 * Console WebSocket (rest-api.md §Console WebSocket)
 * ------------------------------------------------------------------ */

export interface WsFrame<D = unknown> {
  t: "frame";
  topic: string;
  seq: number;
  d: D;
}

export interface WsResult {
  t: "result";
  session_id: string;
  d: { ok: boolean; code?: string; message?: string };
}

export interface WsError {
  t: "error";
  code: string;
  message: string;
}

export type WsServerMessage = WsFrame | WsResult | WsError | { t: "pong" };

/** `fleet.summary` frame payload. */
export interface FleetSummaryFrame {
  devices: Array<{
    id: string;
    online: boolean;
    battery_pct?: number;
    health?: DeviceHealth;
    last_seen_at?: string | null;
    active_run?: ActiveRunRef | null;
  }>;
  counts?: Partial<OverviewPayload["devices"] & OverviewPayload["runs_in_flight"]>;
}

/** `run.<id>` frame payloads, keyed by the event kinds in fleet-v1.md §6. */
export type RunFrame =
  | {
      kind: "run.step";
      run_id?: string;
      n: number;
      max_steps: number;
      line: string;
      observation_hash?: string;
      thinking?: boolean;
      tools?: string[];
      latency_ms?: number;
      tokens?: { prompt: number; completion: number; reasoning: number };
    }
  | { kind: "run.queued"; run_id?: string }
  | { kind: "run.started"; run_id?: string; at?: number }
  | { kind: "run.log"; line: string }
  | { kind: "run.waiting_unlock"; until?: number }
  | {
      kind: "run.finished";
      run_id?: string;
      status: RunStatus;
      summary?: string | null;
      error?: string | null;
      steps?: number;
    };

/** `device.<id>` frame payloads. */
export type DeviceFrame =
  | { kind: "health"; battery_pct?: number; screen_on?: boolean; keyguard_locked?: boolean; a11y_connected?: boolean; charging?: boolean }
  | { kind: "a11y.lost" }
  | { kind: "a11y.restored" }
  | { kind: "killswitch.tripped"; reason?: string }
  | { kind: "control.refused"; reason?: string }
  | { kind: "owner_active" }
  | { kind: "tasks.synced"; task_set_version: number }
  | { kind: "config.applied"; config_version: number }
  | { kind: "stream.stopped"; reason?: string };

/** `dispatch.<id>` frames — per-device acknowledgements as they land. */
export interface DispatchFrame {
  kind: "dispatch.ack";
  device_id: string;
  state: DispatchDeviceState;
  run_id: string | null;
  counts?: Record<DispatchDeviceState, number>;
}
